export const ODATA_COLUMN_TYPES = {
  measure: "MEASURE",
  territorial: "TERRITORIAL",
  dimension: "DIMENSION",
  image: "IMAGE",
  addr: "ADDR",
  lat: "LAT",
  lon: "LON",
  website: "WEBSITE",
  phone: "PHONE",
  note: "NOTE",
  mail: "MAIL",
  coordinates: "COORDINATES",
  dateTime: "DATE",
  time: "TIME",
  day: "DAY",
  month: "MONTH",
  year: "YEAR",
  quarter: "QUARTER",
  quadrimester: "QUADRIMESTER",
  semester: "SEMESTER",
  identifier: "IDENTIFIER"
};

export const normalizeQueryParams = query => {
  if (!query || typeof query !== "string") return null;
  if (query.startsWith("?")) return query;
  return "?" + query;
};

export const getOdataUrl = (baseUrl, dataSource) => `${getCompleteODataBaseUrl(baseUrl)}/odata/${dataSource}`;

export const getOdataMetadataUrl = (baseUrl, dataSource) =>
  `${getOdataUrl(baseUrl, dataSource)}/$metadata?&$format=json`;

export const getOdataDataUrl = (baseUrl, dataSource, mainEntitySet) =>
  `${getOdataUrl(baseUrl, dataSource)}/${mainEntitySet}/$query`;

export const getOdataDataQueryUrl = (baseUrl, dataSource, mainEntitySet, query, count) =>
  `${getOdataUrl(baseUrl, dataSource)}/${mainEntitySet}${normalizeQueryParams(query) ?? ""}${count ? "&$count=true" : ""}`;

// Helper to complete a relative ODataBaseUrl
export const getCompleteODataBaseUrl = baseUrl => {
  if (!baseUrl) return "";

  const stripTrailingSlash = s => s.replace(/\/+$/, "");

  // Already absolute URL (scheme present: http, https, etc...)
  if (/^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(baseUrl)) return stripTrailingSlash(baseUrl);

  // Protocol-relative URL (starts with //)
  if (/^\/\//.test(baseUrl)) {
    const defaultProtocol =
      typeof window !== "undefined" && window.location?.protocol ? window.location.protocol : "https:";
    return stripTrailingSlash(defaultProtocol + baseUrl);
  }

  // Client origin (fallback to empty string if unavailable)
  const clientOrigin =
    typeof window !== "undefined" && window.location?.origin ? stripTrailingSlash(window.location.origin) : "";

  return stripTrailingSlash(clientOrigin + "/" + baseUrl.replace(/^\/+/, ""));
};

// Function that checks whether an item is a valid OData column.
export const isValidColumn = col =>
  col === "$count" ||
  (!col.endsWith("@odata.type") &&
    !col.startsWith("$") &&
    !col.startsWith("@") &&
    !col.includes("@DataPortal") &&
    !col.includes("@Org.OData.Core.V1"));

// Determines the column type according this rules:
// - If the data row defines an explicit @DataPortal.Type, use that value.
// - If the column is present in metadata, use the type defined there.
// - If not present in metadata, check if the @odata.type key exists in the data row.
// - If @odata.type is one of the allowed measure types (Decimal, Single, Double, Int16, Int32, Int64), treat as MEASURE.
// - Otherwise, return the preferred type if provided, or fallback to DIMENSION.
export function getColumnType(metadata, column, dataRow, preferredType) {
  // If the data row defines an explicit DataPortal type, use it before metadata and inference.
  if (dataRow[column + "@DataPortal.Type"]) {
    return dataRow[column + "@DataPortal.Type"];
  }

  // If column is in metadata, use the type defined there
  if (metadata && metadata[column]) {
    return metadata[column]["@DataPortal.Type"] || ODATA_COLUMN_TYPES.dimension;
  }

  // If not in metadata, check for @odata.type in data
  const odataType = dataRow && dataRow[column + "@odata.type"];
  if (odataType) {
    // Extract OData type (e.g., '#Decimal' or 'Edm.Decimal')
    // Normalize the value
    const type = odataType.replace(/^#|^Edm\./, "").toLowerCase();
    const measureTypes = ["decimal", "single", "double", "int16", "int32", "int64"];
    if (measureTypes.includes(type)) {
      return ODATA_COLUMN_TYPES.measure;
    }
  }

  // Otherwise, use the preferred type when available, with DIMENSION as final fallback
  return preferredType || ODATA_COLUMN_TYPES.dimension;
}
